/**
 * Modello per i dati GeoJSON delle schede
 */

// Proprietà di ogni feature (scheda)
export interface SchedaProperties {
  id: number;
  codice: string;
  codicePmo: string;
  codiceAto: string;
  titolo: string;
  rioLocalita: string | null;
  frequenza: string | null;
  annoUltimoIntervento: number | null;
  annoProssimoIntervento: number | null;
  importoUltimoIntervento: number | null;
  comuneId: number;
  comuneNome: string;
  bacinoId: number;
  bacinoNome: string;
  oggettoInterventoId: number | null;
  oggettoInterventoDescrizione: string | null;
  oggettoInterventoCodice: string | null;
  vecchioCodiceUmvs: string | null;

  statoApprovazioneId: number | null;
  statoApprovazioneDescrizione: string | null;
  accessibilitaId: number | null;
  accessibilitaDescrizione: string | null;
  geometriaColore: string | null;
  geometriaMarker: string | null;
}

// Tipi di coordinate per i diversi tipi di geometrie
export type Coordinate = number[];
export type Point = Coordinate;
export type LineString = Coordinate[];
export type Polygon = LineString[];
export type MultiPoint = Coordinate[];
export type MultiLineString = LineString[];
export type MultiPolygon = Polygon[];

// Geometria della scheda - supporta tutti i tipi GeoJSON
export interface SchedaGeometry {
  type: 'Point' | 'MultiPoint' | 'LineString' | 'MultiLineString' | 'Polygon' | 'MultiPolygon' | 'GeometryCollection';
  coordinates?: Point | MultiPoint | LineString | MultiLineString | Polygon | MultiPolygon | any;
  geometries?: any[]; // Per GeometryCollection
}

// Singola feature che rappresenta una scheda
export interface SchedaFeature {
  type: 'Feature';
  properties: SchedaProperties;
  geometry: SchedaGeometry;
}

// Collezione di feature (schede)
export interface SchedeFeatureCollection {
  type: 'FeatureCollection';
  features: SchedaFeature[];
}

// Risposta del servizio getSchede
export interface SchedeResponse {
  features?: SchedaFeature[];
  // Altri possibili campi che potrebbero essere presenti nella risposta
  [key: string]: any;
}

/**
 * Classe di utilità per lavorare con i dati GeoJSON delle schede
 */
export class SchedaGeoJsonUtils {
  /**
   * Converte la risposta del servizio in una FeatureCollection GeoJSON standard
   * e assicura che sia un GeoJSON valido
   */
  static toFeatureCollection(response: SchedeResponse | any): SchedeFeatureCollection {
    // Se la risposta è già una FeatureCollection valida, restituiscila
    if (response && response.type === 'FeatureCollection' && Array.isArray(response.features)) {
      return this.normalizzaFeatureCollection(response as SchedeFeatureCollection);
    }

    // Se abbiamo un array di features
    if (response && Array.isArray(response.features)) {
      return this.normalizzaFeatureCollection({
        type: 'FeatureCollection',
        features: response.features
      });
    }

    // Se abbiamo solo una singola feature
    if (response && (response.type === 'Feature' || response.properties)) {
      return {
        type: 'FeatureCollection',
        features: [this.normalizzaFeature(response)]
      };
    }

    // Se abbiamo un array di oggetti (probabilmente non ancora formattati come features)
    if (Array.isArray(response)) {
      return {
        type: 'FeatureCollection',
        features: response.map(item => this.convertiInFeature(item))
      };
    }

    // Se nessuna delle condizioni precedenti è soddisfatta, restituisci una collezione vuota
    console.error('Impossibile convertire la risposta in un GeoJSON valido', response);
    return { type: 'FeatureCollection', features: [] };
  }

  /**
   * Converte un oggetto generico in una Feature GeoJSON
   */
  private static convertiInFeature(item: any): SchedaFeature {
    // Se l'oggetto è già una feature, normalizzala
    if (item.type === 'Feature') {
      return this.normalizzaFeature(item);
    }

    // Estrai le proprietà e la geometria dall'oggetto
    const properties: SchedaProperties = {
      id: item.id || 0,
      codice: item.codice || '',
      codicePmo: item.codicePmo || '',
      codiceAto: item.codiceAto || '',
      titolo: item.titolo || '',
      rioLocalita: item.rioLocalita || null,
      frequenza: item.frequenza || null,
      annoUltimoIntervento: item.annoUltimoIntervento || null,
      annoProssimoIntervento: item.annoProssimoIntervento || null,
      importoUltimoIntervento: item.importoUltimoIntervento || null,
      comuneId: item.comuneId || 0,
      comuneNome: item.comuneNome || '',
      bacinoId: item.bacinoId || 0,
      bacinoNome: item.bacinoNome || '',
      oggettoInterventoId: item.oggettoInterventoId || null,
      oggettoInterventoDescrizione: item.oggettoInterventoDescrizione || null,
      oggettoInterventoCodice: item.oggettoInterventoCodice || null,
      vecchioCodiceUmvs: item.vecchioCodiceUmvs || null,
      statoApprovazioneId: item.statoApprovazioneId || null,
      statoApprovazioneDescrizione: item.statoApprovazioneDescrizione || null,
      accessibilitaId: item.accessibilitaId || null,
      accessibilitaDescrizione: item.accessibilitaDescrizione || null,
      geometriaColore: item.geometriaColore || null,
      geometriaMarker: item.geometriaMarker || null
    };

    // Cerca di estrarre la geometria, se presente
    let geometry: SchedaGeometry | null = null;

    if (item.geometry) {
      geometry = item.geometry;
    } else if (item.coordinates) {
      // Se ci sono coordinate dirette nell'oggetto, crea una geometria Point
      geometry = {
        type: 'Point',
        coordinates: item.coordinates
      };
    }

    // Se non è stata trovata nessuna geometria, crea una geometria nulla
    if (!geometry) {
      geometry = {
        type: 'Point',
        coordinates: [0, 0]
      };
    }

    // Assicurati che la geometria abbia un tipo valido
    if (!geometry.type) {
      geometry.type = 'Point';
    }

    return {
      type: 'Feature',
      properties,
      geometry: geometry as SchedaGeometry
    };
  }

  /**
   * Normalizza una Feature GeoJSON per assicurarsi che sia valida
   */
  private static normalizzaFeature(feature: any): SchedaFeature {
    if (!feature) {
      return this.creaFeatureVuota();
    }

    // Se la feature ha già proprietà complete, usale, altrimenti crea proprietà complete
    const props = feature.properties || {};
    const properties: SchedaProperties = {
      id: props.id || 0,
      codice: props.codice || '',
      codicePmo: props.codicePmo || '',
      codiceAto: props.codiceAto || '',
      titolo: props.titolo || '',
      rioLocalita: props.rioLocalita || null,
      frequenza: props.frequenza || null,
      annoUltimoIntervento: props.annoUltimoIntervento || null,
      annoProssimoIntervento: props.annoProssimoIntervento || null,
      importoUltimoIntervento: props.importoUltimoIntervento || null,
      comuneId: props.comuneId || 0,
      comuneNome: props.comuneNome || '',
      bacinoId: props.bacinoId || 0,
      bacinoNome: props.bacinoNome || '',
      oggettoInterventoId: props.oggettoInterventoId || null,
      oggettoInterventoDescrizione: props.oggettoInterventoDescrizione || null,
      oggettoInterventoCodice: props.oggettoInterventoCodice || null,
      vecchioCodiceUmvs: props.vecchioCodiceUmvs || null,
      statoApprovazioneId: props.statoApprovazioneId || null,
      statoApprovazioneDescrizione: props.statoApprovazioneDescrizione || null,
      accessibilitaId: props.accessibilitaId || null,
      accessibilitaDescrizione: props.accessibilitaDescrizione || null,
      geometriaColore: props.geometriaColore || null,
      geometriaMarker: props.geometriaMarker || null
    };

    // Assicurati che il tipo sia 'Feature'
    const normalizedFeature: SchedaFeature = {
      type: 'Feature',
      properties,
      geometry: feature.geometry || { type: 'Point', coordinates: [0, 0] }
    };

    // Assicurati che la geometria abbia un tipo valido
    if (!normalizedFeature.geometry.type) {
      normalizedFeature.geometry.type = 'Point';
    }

    // Assicurati che ci siano coordinate valide se necessarie
    if (normalizedFeature.geometry.type !== 'GeometryCollection' && !normalizedFeature.geometry.coordinates) {
      normalizedFeature.geometry.coordinates = [0, 0];
    }

    return normalizedFeature;
  }

  /**
   * Normalizza una FeatureCollection GeoJSON per assicurarsi che sia valida
   */
  private static normalizzaFeatureCollection(collection: any): SchedeFeatureCollection {
    if (!collection) {
      return { type: 'FeatureCollection', features: [] };
    }

    // Assicurati che il tipo sia 'FeatureCollection'
    const normalizedCollection: SchedeFeatureCollection = {
      type: 'FeatureCollection',
      features: Array.isArray(collection.features)
        ? collection.features.map((f: any) => this.normalizzaFeature(f))
        : []
    };

    return normalizedCollection;
  }

  /**
   * Crea una Feature vuota con valori predefiniti
   */
  private static creaFeatureVuota(): SchedaFeature {
    return {
      type: 'Feature',
      properties: {
        id: 0,
        codice: '',
        codicePmo: '',
        codiceAto: '',
        titolo: '',
        rioLocalita: null,
        frequenza: null,
        annoUltimoIntervento: null,
        annoProssimoIntervento: null,
        importoUltimoIntervento: null,
        comuneId: 0,
        comuneNome: '',
        bacinoId: 0,
        bacinoNome: '',
        oggettoInterventoId: null,
        oggettoInterventoDescrizione: null,
        oggettoInterventoCodice: null,
        vecchioCodiceUmvs: null,
        statoApprovazioneId: null,
        statoApprovazioneDescrizione: null,
        accessibilitaId: null,
        accessibilitaDescrizione: null,
        geometriaColore: null,
        geometriaMarker: null
      },
      geometry: {
        type: 'Point',
        coordinates: [0, 0]
      }
    };
  }

  /**
   * Ottiene il centro di una geometria (punto centrale approssimativo)
   * Supporta diversi tipi di geometrie
   */
  static getCentroGeometria(feature: SchedaFeature): [number, number] {
    try {
      if (!feature || !feature.geometry) {
        return [0, 0];
      }

      switch (feature.geometry.type) {
        case 'Point':
          return feature.geometry.coordinates as [number, number];

        case 'MultiPoint':
          const points = feature.geometry.coordinates as number[][];
          return this.calcolaMediaCoordinate(points);

        case 'LineString':
          const line = feature.geometry.coordinates as number[][];
          return this.calcolaMediaCoordinate(line);

        case 'MultiLineString':
          const lines = feature.geometry.coordinates as number[][][];
          return this.calcolaMediaCoordinate(lines.flat());

        case 'Polygon':
          const polygon = feature.geometry.coordinates[0] as number[][];
          return this.calcolaMediaCoordinate(polygon);

        case 'MultiPolygon':
          const coords = (feature.geometry.coordinates[0][0]) as number[][];
          return this.calcolaMediaCoordinate(coords);

        case 'GeometryCollection':
          // Per le collezioni di geometrie, utilizza la prima geometria disponibile
          if (feature.geometry.geometries && feature.geometry.geometries.length > 0) {
            return this.getCentroGeometria({
              type: 'Feature',
              properties: feature.properties,
              geometry: feature.geometry.geometries[0]
            });
          }
          return [0, 0];

        default:
          return [0, 0];
      }
    } catch (e) {
      console.error('Errore nel calcolo del centro della geometria', e);
      return [0, 0]; // Fallback
    }
  }

  /**
   * Calcola la media delle coordinate
   */
  private static calcolaMediaCoordinate(coords: number[][]): [number, number] {
    if (!coords || coords.length === 0) {
      return [0, 0];
    }

    let sumLng = 0, sumLat = 0;
    for (const coord of coords) {
      if (Array.isArray(coord) && coord.length >= 2) {
        sumLng += coord[0];
        sumLat += coord[1];
      }
    }
    return [
      sumLng / coords.length,
      sumLat / coords.length
    ];
  }

  /**
   * Verifica se un oggetto è un GeoJSON valido
   */
  static isValidGeoJSON(obj: any): boolean {
    if (!obj) {
      return false;
    }

    // Verifica se è una FeatureCollection
    if (obj.type === 'FeatureCollection') {
      return Array.isArray(obj.features);
    }

    // Verifica se è una Feature
    if (obj.type === 'Feature') {
      return obj.geometry !== undefined;
    }

    // Verifica se è una Geometria
    const validGeometryTypes = [
      'Point', 'MultiPoint', 'LineString', 'MultiLineString',
      'Polygon', 'MultiPolygon', 'GeometryCollection'
    ];

    return validGeometryTypes.includes(obj.type);
  }

  /**
   * Standardizza le geometrie di una FeatureCollection per un tipo specifico di visualizzazione
   * Questo è utile quando il layer della mappa richiede un tipo specifico di geometria
   *
   * @param featureCollection La collezione di feature da standardizzare
   * @param targetType Il tipo di geometria desiderato ('line', 'point', 'polygon')
   * @returns Una nuova FeatureCollection con geometrie standardizzate
   */
  static standardizzaGeometrie(featureCollection: SchedeFeatureCollection, targetType: 'line' | 'point' | 'polygon'): SchedeFeatureCollection {
    if (!featureCollection || !featureCollection.features) {
      return { type: 'FeatureCollection', features: [] };
    }

    const standardizedFeatures = featureCollection.features.map(feature => {
      const standardizedFeature = { ...feature };

      // Se non c'è geometria, crea una geometria vuota del tipo richiesto
      if (!feature.geometry) {
        standardizedFeature.geometry = this.creaGeometriaVuota(targetType);
        return standardizedFeature;
      }

      // Standardizza la geometria in base al tipo richiesto
      standardizedFeature.geometry = this.convertiGeometria(feature.geometry, targetType);
      return standardizedFeature;
    });

    return {
      type: 'FeatureCollection',
      features: standardizedFeatures
    };
  }

  /**
   * Converte una geometria in un tipo specifico
   */
  private static convertiGeometria(geometry: SchedaGeometry, targetType: 'line' | 'point' | 'polygon'): SchedaGeometry {
    try {
      if (!geometry || !geometry.type) {
        return this.creaGeometriaVuota(targetType);
      }

      // Se la geometria è già del tipo richiesto, restituiscila così com'è
      switch (targetType) {
        case 'line':
          if (geometry.type === 'LineString' || geometry.type === 'MultiLineString') {
            return geometry;
          }
          break;
        case 'point':
          if (geometry.type === 'Point' || geometry.type === 'MultiPoint') {
            return geometry;
          }
          break;
        case 'polygon':
          if (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon') {
            return geometry;
          }
          break;
      }

      // Conversione da altri tipi al tipo richiesto
      switch (targetType) {
        case 'line':
          return this.convertiInLineString(geometry);
        case 'point':
          return this.convertiInPoint(geometry);
        case 'polygon':
          return this.convertiInPolygon(geometry);
        default:
          return geometry;
      }
    } catch (e) {
      console.error('Errore nella conversione della geometria', e);
      return this.creaGeometriaVuota(targetType);
    }
  }

  /**
   * Crea una geometria vuota di un tipo specifico
   */
  private static creaGeometriaVuota(type: 'line' | 'point' | 'polygon'): SchedaGeometry {
    switch (type) {
      case 'line':
        return {
          type: 'LineString',
          coordinates: [[0, 0], [0, 0]]
        };
      case 'point':
        return {
          type: 'Point',
          coordinates: [0, 0]
        };
      case 'polygon':
        return {
          type: 'Polygon',
          coordinates: [[[0, 0], [0, 1], [1, 1], [1, 0], [0, 0]]]
        };
    }
  }

  /**
   * Converte una geometria in LineString o MultiLineString
   */
  private static convertiInLineString(geometry: SchedaGeometry): SchedaGeometry {
    switch (geometry.type) {
      case 'Point':
        // Crea una LineString con due punti identici
        const point = geometry.coordinates as Point;
        return {
          type: 'LineString',
          coordinates: [point, point]
        };

      case 'MultiPoint':
        // Connetti i punti in una LineString
        const points = geometry.coordinates as MultiPoint;
        if (points.length < 2) {
          // Se c'è un solo punto, crea una linea con due punti identici
          return {
            type: 'LineString',
            coordinates: points.length === 1 ? [points[0], points[0]] : [[0, 0], [0, 0]]
          };
        }
        return {
          type: 'LineString',
          coordinates: points
        };

      case 'Polygon':
        // Usa il contorno esterno del poligono come LineString
        const polygon = geometry.coordinates as Polygon;
        return {
          type: 'LineString',
          coordinates: polygon[0] || [[0, 0], [0, 0]]
        };

      case 'MultiPolygon':
        // Raccoglie tutti i contorni esterni e li combina in un MultiLineString
        const multiPolygon = geometry.coordinates as MultiPolygon;
        const lines = multiPolygon.map(polygon => polygon[0] || [[0, 0], [0, 0]]);
        return {
          type: 'MultiLineString',
          coordinates: lines
        };

      case 'GeometryCollection':
        // Converte la prima geometria disponibile
        if (geometry.geometries && geometry.geometries.length > 0) {
          return this.convertiInLineString(geometry.geometries[0]);
        }
        return this.creaGeometriaVuota('line');

      default:
        // LineString e MultiLineString rimangono invariati
        return geometry;
    }
  }

  /**
   * Converte una geometria in Point o MultiPoint
   */
  private static convertiInPoint(geometry: SchedaGeometry): SchedaGeometry {
    switch (geometry.type) {
      case 'LineString':
        // Prendi il punto centrale della linea
        const line = geometry.coordinates as LineString;
        const centroLine = this.calcolaMediaCoordinate(line);
        return {
          type: 'Point',
          coordinates: centroLine
        };

      case 'MultiLineString':
        // Prendi i punti centrali di tutte le linee
        const lines = geometry.coordinates as MultiLineString;
        const centroLines = lines.map(line => this.calcolaMediaCoordinate(line));
        return {
          type: 'MultiPoint',
          coordinates: centroLines
        };

      case 'Polygon':
        // Calcola il centro del poligono
        const polygon = geometry.coordinates as Polygon;
        const centroPolygon = this.calcolaMediaCoordinate(polygon[0] || [[0, 0], [0, 0]]);
        return {
          type: 'Point',
          coordinates: centroPolygon
        };

      case 'MultiPolygon':
        // Calcola i centri di tutti i poligoni
        const multiPolygon = geometry.coordinates as MultiPolygon;
        const centroMultiPolygon = multiPolygon.map(polygon =>
          this.calcolaMediaCoordinate(polygon[0] || [[0, 0], [0, 0]])
        );
        return {
          type: 'MultiPoint',
          coordinates: centroMultiPolygon
        };

      case 'GeometryCollection':
        // Converte la prima geometria disponibile
        if (geometry.geometries && geometry.geometries.length > 0) {
          return this.convertiInPoint(geometry.geometries[0]);
        }
        return this.creaGeometriaVuota('point');

      default:
        // Point e MultiPoint rimangono invariati
        return geometry;
    }
  }

  /**
   * Converte una geometria in Polygon o MultiPolygon
   */
  private static convertiInPolygon(geometry: SchedaGeometry): SchedaGeometry {
    switch (geometry.type) {
      case 'Point':
        // Crea un piccolo poligono attorno al punto
        const point = geometry.coordinates as Point;
        const buffer = 0.0001; // ~10m a seconda della latitudine
        return {
          type: 'Polygon',
          coordinates: [[
            [point[0] - buffer, point[1] - buffer],
            [point[0] + buffer, point[1] - buffer],
            [point[0] + buffer, point[1] + buffer],
            [point[0] - buffer, point[1] + buffer],
            [point[0] - buffer, point[1] - buffer] // Chiude il poligono
          ]]
        };

      case 'MultiPoint':
        // Crea un poligono per ogni punto
        const points = geometry.coordinates as MultiPoint;
        const buffer2 = 0.0001;
        const polygons = points.map(point => [[
          [point[0] - buffer2, point[1] - buffer2],
          [point[0] + buffer2, point[1] - buffer2],
          [point[0] + buffer2, point[1] + buffer2],
          [point[0] - buffer2, point[1] + buffer2],
          [point[0] - buffer2, point[1] - buffer2] // Chiude il poligono
        ]]);
        return {
          type: 'MultiPolygon',
          coordinates: polygons
        };

      case 'LineString':
        // Crea un poligono chiudendo la linea
        const line = geometry.coordinates as LineString;
        if (line.length < 3) {
          // Se la linea ha meno di 3 punti, non possiamo creare un poligono valido
          return this.creaGeometriaVuota('polygon');
        }
        // Controlla se la linea è già chiusa
        const primoP = line[0];
        const ultimoP = line[line.length - 1];
        const lineaCopia = [...line];

        if (primoP[0] !== ultimoP[0] || primoP[1] !== ultimoP[1]) {
          // Chiudi il poligono aggiungendo il primo punto alla fine
          lineaCopia.push(primoP);
        }

        return {
          type: 'Polygon',
          coordinates: [lineaCopia]
        };

      case 'MultiLineString':
        // Crea un poligono per ogni linea
        const lines = geometry.coordinates as MultiLineString;
        const polygons2 = lines.map(line => {
          if (line.length < 3) {
            // Line non valida per un poligono
            return [[[0, 0], [0, 1], [1, 1], [1, 0], [0, 0]]];
          }

          const primoP = line[0];
          const ultimoP = line[line.length - 1];
          const lineaCopia = [...line];

          if (primoP[0] !== ultimoP[0] || primoP[1] !== ultimoP[1]) {
            lineaCopia.push(primoP);
          }

          return [lineaCopia];
        });

        return {
          type: 'MultiPolygon',
          coordinates: polygons2
        };

      case 'GeometryCollection':
        // Converte la prima geometria disponibile
        if (geometry.geometries && geometry.geometries.length > 0) {
          return this.convertiInPolygon(geometry.geometries[0]);
        }
        return this.creaGeometriaVuota('polygon');

      default:
        // Polygon e MultiPolygon rimangono invariati
        return geometry;
    }
  }
}
